import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTranslations } from "@/i18n/IntlProvider";
import { CodeThumbnail } from "@/components/CodeThumbnail";

/**
 * Shape mirrors QrCodeResponse on the Spring side
 * (modules/qrcode/.../api/QrCodeDtos.kt). The list endpoint
 * `GET /v1/codes` returns a flat array — no envelope.
 */
type QrMode = "STATIC" | "DYNAMIC";
type QrStatus = "ACTIVE" | "EXPIRED" | "RATE_LIMITED" | "ARCHIVED";
type QrType = "URL" | "TEXT" | "EMAIL" | "SMS" | "GEO" | "WIFI" | "VCARD" | "FILE";

type QrCode = {
  id: string;
  type: QrType | string;
  mode: QrMode;
  status: QrStatus;
  createdAt: string;
  archivedAt: string | null;
  shortSlug: string | null;
  expiresAt: string | null;
  passwordProtected: boolean;
  locked: boolean;
  lockedAt: string | null;
  payload: Record<string, unknown>;
};

type PlanQuota = {
  feature: string;
  limit: number | null;
  used: number | null;
  remaining: number | null;
};

type PlanResponse = {
  planId: string;
  quotas: PlanQuota[];
};

/**
 * /$locale/codes — read-only table of the signed-in account's codes.
 *
 * Mirrors the legacy web/src/components/app/codes-list-panel.tsx so the
 * visual contract is identical: filter-bar (includeArchived + mode +
 * type), then a table with a 40 px QR thumbnail in the first column and
 * status + locked/draft pills in the status column. Clicking the
 * thumbnail (or the row hover area) opens the detail page.
 *
 * Data goes through `api()` from @/lib/api so the BFF session cookie
 * travels with the request — we don't use @qrody/api-client.listCodes
 * here because its authedFetch still wires a Bearer-token from oidc-spa
 * (Next-bound), which no longer applies under the BFF flow.
 */
export function CodesListRoute() {
  const { locale } = useParams({ strict: false }) as { locale?: string };
  const lang = locale ?? "de";
  const t = useTranslations("app.codes");

  const [includeArchived, setIncludeArchived] = useState(false);
  const [mode, setMode] = useState<QrMode | "">("");
  const [type, setType] = useState<QrType | "">("");

  const queryPath = useMemo(() => {
    const qs = new URLSearchParams();
    if (includeArchived) qs.set("includeArchived", "true");
    if (mode) qs.set("mode", mode);
    if (type) qs.set("type", type);
    const search = qs.toString();
    return search.length > 0 ? `/v1/codes?${search}` : "/v1/codes";
  }, [includeArchived, mode, type]);

  const codesQuery = useQuery({
    queryKey: ["codes", "list", { includeArchived, mode, type }],
    queryFn: () => api<QrCode[]>(queryPath),
    retry: false,
  });

  const rows = codesQuery.data ?? [];
  const empty = !codesQuery.isPending && !codesQuery.error && rows.length === 0;

  // Plan-quota probe — surfaces "X / Y dynamische Codes" next to the
  // title so the user sees the cap before they hit the 402 on /codes/new.
  // Same query-key as the billing page so the two surfaces share cache.
  const planQuery = useQuery({
    queryKey: ["billing-plan"],
    queryFn: () => api<PlanResponse>("/v1/billing/plan"),
    retry: false,
    staleTime: 60_000,
  });
  const codesQuota = planQuery.data?.quotas.find(
    (q) => q.feature === "qr.dynamic.max_active",
  );

  return (
    <section className="mx-auto max-w-screen-xl space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t("title")}</h1>
          {codesQuota && codesQuota.limit !== null ? (
            <p className="text-xs tabular-nums text-muted-foreground">
              {t("quotaInline", {
                used: String(codesQuota.used ?? 0),
                limit: String(codesQuota.limit),
              })}
            </p>
          ) : null}
        </div>
        <Link
          to={"/$locale/codes/new" as never}
          params={{ locale: lang } as never}
          className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
        >
          {t("newCta")}
        </Link>
      </header>

      <FilterBar
        includeArchived={includeArchived}
        mode={mode}
        type={type}
        onIncludeArchived={setIncludeArchived}
        onMode={setMode}
        onType={setType}
      />

      {codesQuery.error ? (
        <ErrorNotice
          message={(codesQuery.error as Error).message}
          onRetry={() => void codesQuery.refetch()}
        />
      ) : null}

      {empty ? (
        <EmptyState locale={lang} />
      ) : (
        <CodesTable rows={rows} loading={codesQuery.isPending} locale={lang} />
      )}
    </section>
  );
}

function FilterBar({
  includeArchived,
  mode,
  type,
  onIncludeArchived,
  onMode,
  onType,
}: {
  includeArchived: boolean;
  mode: QrMode | "";
  type: QrType | "";
  onIncludeArchived: (v: boolean) => void;
  onMode: (v: QrMode | "") => void;
  onType: (v: QrType | "") => void;
}) {
  const t = useTranslations("app.codes.filter");
  const types: QrType[] = ["URL", "TEXT", "EMAIL", "SMS", "GEO", "WIFI", "VCARD", "FILE"];
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3">
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => onIncludeArchived(e.target.checked)}
        />
        {t("includeArchived")}
      </label>
      <select
        value={mode}
        onChange={(e) => onMode(e.target.value as QrMode | "")}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      >
        <option value="">{t("modeAll")}</option>
        <option value="STATIC">{t("modeStatic")}</option>
        <option value="DYNAMIC">{t("modeDynamic")}</option>
      </select>
      <select
        value={type}
        onChange={(e) => onType(e.target.value as QrType | "")}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      >
        <option value="">{t("typeAll")}</option>
        {types.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    </div>
  );
}

function EmptyState({ locale }: { locale: string }) {
  const t = useTranslations("app.codes.empty");
  return (
    <div className="rounded-lg border border-dashed border-border p-10 text-center">
      <h2 className="text-lg font-semibold">{t("title")}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{t("desc")}</p>
      <Link
        to={"/$locale/codes/new" as never}
        params={{ locale } as never}
        className="mt-6 inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t("cta")}
      </Link>
    </div>
  );
}

function CodesTable({
  rows,
  loading,
  locale,
}: {
  rows: QrCode[];
  loading: boolean;
  locale: string;
}) {
  const t = useTranslations("app.codes");

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-6 text-sm text-muted-foreground">
        …
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">QR</th>
            <th className="px-4 py-2 font-medium">{t("table.type")}</th>
            <th className="px-4 py-2 font-medium">{t("table.mode")}</th>
            <th className="px-4 py-2 font-medium">{t("table.status")}</th>
            <th className="px-4 py-2 font-medium">{t("table.slug")}</th>
            <th className="px-4 py-2 font-medium">{t("table.created")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <td className="px-3 py-2">
                <Link
                  to={"/$locale/codes/$id" as never}
                  params={{ locale, id: row.id } as never}
                  aria-label={t("action.detail")}
                  className="inline-flex rounded-md outline-none ring-offset-background transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <CodeThumbnail
                    id={row.id}
                    size={40}
                    cacheKey={JSON.stringify(row.payload)}
                  />
                </Link>
              </td>
              <td className="px-4 py-2">{row.type}</td>
              <td className="px-4 py-2">
                {t(`mode.${row.mode}` as `mode.${typeof row.mode}`)}
              </td>
              <td className="px-4 py-2">
                <span className="inline-flex flex-wrap items-center gap-1">
                  <StatusBadge status={row.status} />
                  {row.locked ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                      Locked
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
                      Draft
                    </span>
                  )}
                </span>
              </td>
              <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                {row.shortSlug ?? "—"}
              </td>
              <td className="px-4 py-2 text-muted-foreground">{formatDate(row.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: QrCode["status"] }) {
  const t = useTranslations("app.codes.status");
  const tone =
    status === "ACTIVE"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : status === "ARCHIVED"
        ? "bg-muted text-muted-foreground border-border"
        : "bg-amber-50 text-amber-900 border-amber-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {t(status)}
    </span>
  );
}

function ErrorNotice({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useTranslations("common");
  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex items-start justify-between gap-4 rounded-md border border-orange-300 bg-orange-50 px-4 py-3 text-sm text-orange-900"
    >
      <p>
        {t("error")}: {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
      >
        {t("retry")}
      </button>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
